import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useSkillSheetMainStepper } from "../context/SkillSheetMainStepperContext";
import RequiredLabel from "../../shared/RequiredLabel";
import NumberInputField from "../../shared/NumberInputField";
import FormMultilineField from "../../shared/FormMultilineField";
import FreeSoloMultiSelect from "../../shared/FreeSoloMultiSelect";

export default function ProjectInfoForm({ isEdit = false }) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper(true);
    } catch (error) {
      console.error("編集用コンテキストの取得に失敗しました:", error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper(false);
    } catch (error) {
      console.error("作成用コンテキストの取得に失敗しました:", error);
      throw error;
    }
  }

  // 他ユーザー編集時の情報をコンテキストから取得
  const { targetUserId, targetUserType } = stepperContext;

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    masterData,
    validateStep,
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 期間重複警告の状態
  const [periodOverlapWarning, setPeriodOverlapWarning] = useState(false);
  // 既存work_historyの期間情報
  const [existingPeriods, setExistingPeriods] = useState({
    earliest_start: null,
    latest_end: null,
    has_existing_periods: false,
  });

  // フォームの値を監視
  const name = watch("name");
  const period_from = watch("period_from");
  const period_at = watch("period_at");
  const participation_flag = watch("participation_flag");
  const development_methods = watch("development_methods") || [];
  const sprint_cycle_week = watch("sprint_cycle_week");
  const roles = watch("roles") || [];
  const project_scale = watch("project_scale");
  const team_scale = watch("team_scale");
  const industry_ids = watch("industry_ids") || [];
  const achievement = watch("achievement");

  // id配列を options から {id, name} 形式に変換（FreeSoloMultiSelect 表示用）
  const buildSelectedOptions = (ids, options) => {
    const map = new Map((options || []).map((o) => [String(o.id), o]));

    return (ids || [])
      .map((id) => map.get(String(id)))
      .filter(Boolean)
      .map((o) => ({ id: o.id, name: o.name }));
  };

  // industry_ids（id配列）→ FreeSoloMultiSelect 用の表示データ
  const industryValue = useMemo(
    () => buildSelectedOptions(industry_ids, masterData.industries),
    [industry_ids, masterData.industries],
  );

  // 既存プロジェクトの期間を取得する関数
  const fetchExistingPeriods = async () => {
    try {
      let url = "/skill_sheet_mains/check_overlaps";
      // check_overlaps 用のクエリパラメータ
      const params = new URLSearchParams();

      // 編集時は編集対象のプロジェクトを除外するため、work_history_idをパラメータに追加
      if (isEdit && stepperContext.workHistory?.id) {
        params.append("work_history_id", stepperContext.workHistory.id);
      }

      // 他ユーザー（User / CompanyUser）編集時の対象ユーザーID
      if (targetUserType === "user") {
        params.append("user_id", targetUserId);
      } else {
        params.append("company_user_id", targetUserId);
      }

      if ([...params].length > 0) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      setExistingPeriods(data);
      return data;
    } catch (error) {
      console.error("Error fetching existing periods:", error);
      return {
        earliest_start: null,
        latest_end: null,
        has_existing_periods: false,
      };
    }
  };

  // 期間重複チェック関数
  const checkPeriodOverlap = (
    startDate,
    endDate,
    participationFlag = false,
  ) => {
    if (!existingPeriods.has_existing_periods || !startDate) {
      return false;
    }

    const inputStart = new Date(startDate);
    const inputEnd = participationFlag
      ? new Date()
      : endDate
        ? new Date(endDate)
        : null;

    if (!inputEnd) {
      return false;
    }

    const existingStart = new Date(existingPeriods.earliest_start);
    const existingEnd = new Date(existingPeriods.latest_end);

    // 重複判定: 新しい期間が既存期間と1日でも重複する場合
    return inputStart <= existingEnd && inputEnd >= existingStart;
  };

  // マスタ取得
  useEffect(() => {
    const masters = ["roles", "industries", "development_methods"];

    masters.forEach((key) => {
      if (!masterData[key]?.length) {
        fetchMasterData(key);
      }
    });
  }, [
    fetchMasterData,
    masterData.roles,
    masterData.industries,
    masterData.development_methods,
  ]);

  // 初回マウント時と対象ユーザーまたは編集対象 workHistory が変わったときに既存期間を再取得
  useEffect(() => {
    fetchExistingPeriods();
  }, [targetUserId, targetUserType, stepperContext.workHistory?.id]);

  // 期間重複チェック
  useEffect(() => {
    if (period_from && (period_at || participation_flag)) {
      const hasOverlap = checkPeriodOverlap(
        period_from,
        period_at,
        participation_flag,
      );
      setPeriodOverlapWarning(hasOverlap);
    } else {
      setPeriodOverlapWarning(false);
    }
  }, [period_from, period_at, participation_flag, existingPeriods]);

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // 必須項目チェック
    if (!name || name.trim() === "") {
      setError("name", {
        type: "required",
        message: "プロジェクト名は入力必須です。",
      });
      hasError = true;
    }

    if (!period_from) {
      setError("period_from", {
        type: "required",
        message: "開始日は入力必須です。",
      });
      hasError = true;
    }

    if (!participation_flag && !period_at) {
      setError("period_at", {
        type: "required",
        message: "終了日は入力必須です。",
      });
      hasError = true;
    }

    if (isScrumSelected) {
      if (!sprint_cycle_week || sprint_cycle_week <= 0) {
        setError("sprint_cycle_week", {
          type: "required",
          message: "スプリントの周期は入力必須です。",
        });
        hasError = true;
      }
    }

    if (!development_methods || development_methods.length === 0) {
      setError("development_methods", {
        type: "required",
        message: "開発プロセスは選択必須です。",
      });
      hasError = true;
    }

    if (!roles || roles.length === 0) {
      setError("roles", {
        type: "required",
        message: "役割は選択必須です。",
      });
      hasError = true;
    }

    if (!project_scale || project_scale <= 0) {
      setError("project_scale", {
        type: "required",
        message: "プロジェクト規模（人）は入力必須です。",
      });
      hasError = true;
    }

    if (!team_scale || team_scale <= 0) {
      setError("team_scale", {
        type: "required",
        message: "チーム規模（人）は入力必須です。",
      });
      hasError = true;
    }

    // 参画中の場合の開始日バリデーション
    if (participation_flag && period_from) {
      const today = new Date();
      const todayString =
        today.getFullYear() +
        "-" +
        String(today.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(today.getDate()).padStart(2, "0");

      if (period_from > todayString) {
        setError("period_from", {
          type: "participatingDateRange",
          message: "参画中の場合、開始日は今日以前の日付を入力してください。",
        });
        hasError = true;
      }
    }

    // 日付バリデーション
    if (period_from && period_at && !participation_flag) {
      const startDate = new Date(period_from);
      const endDate = new Date(period_at);

      // 開始日と終了日が同じ日付の場合はバリデーションしない
      if (startDate > endDate) {
        setError("period_at", {
          type: "dateRange",
          message: "終了日は開始日より後の日付を入力してください。",
        });
        hasError = true;
      }
    }

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、画面の上部にスクロール
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(0, validateForm);
    }
  }, [
    validateStep,
    name,
    period_from,
    period_at,
    participation_flag,
    development_methods,
    sprint_cycle_week,
    roles,
    project_scale,
    team_scale,
  ]);

  const handleChange = (fieldName, value) => {
    setValue(fieldName, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    // 変更時に該当フィールドのエラーをクリア
    if (errors[fieldName]) {
      clearErrors(fieldName);
    }
  };

  // 必須項目のバリデーションエラー表示
  const nameError = showValidation && errors.name;
  const periodFromError = showValidation && errors.period_from;
  const periodAtError = showValidation && errors.period_at;
  const developmentMethodError = showValidation && errors.development_methods;
  const roleError = showValidation && errors.roles;
  const projectScaleError = showValidation && errors.project_scale;
  const teamScaleError = showValidation && errors.team_scale;
  const sprintCycleWeekError = showValidation && errors.sprint_cycle_week;

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (nameError) validationErrors.push(nameError.message);
  if (periodFromError) validationErrors.push(periodFromError.message);
  if (periodAtError) validationErrors.push(periodAtError.message);
  if (developmentMethodError)
    validationErrors.push(developmentMethodError.message);
  if (roleError) validationErrors.push(roleError.message);
  if (projectScaleError) validationErrors.push(projectScaleError.message);
  if (teamScaleError) validationErrors.push(teamScaleError.message);
  if (sprintCycleWeekError) validationErrors.push(sprintCycleWeekError.message);

  // 選択にスクラムが含まれるか
  const isScrumSelected = useMemo(() => {
    const arr = Array.isArray(development_methods) ? development_methods : [];
    return arr.some((it) => {
      const name = typeof it === "string" ? it : it?.name;
      return (name || "").trim().toLowerCase() === "スクラム";
    });
  }, [development_methods]);

  // スクラムが外れたら値とエラーをクリア
  useEffect(() => {
    if (!isScrumSelected) {
      setValue("sprint_cycle_week", null);
      if (typeof clearErrors === "function") clearErrors("sprint_cycle_week");
    }
  }, [isScrumSelected, setValue, clearErrors]);

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        sx={{ marginBottom: "24px" }}
      >
        プロジェクト情報
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* 期間重複警告メッセージの表示 */}
      {periodOverlapWarning && (
        <Alert severity="info" sx={{ mb: 2 }}>
          登録済みプロジェクトの期間と重複しています
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* プロジェクト名 */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>プロジェクト名</Typography>
              <RequiredLabel show={true} />
            </Box>
            <TextField
              required
              fullWidth
              id="name"
              name="name"
              value={name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Webアプリケーション開発プロジェクト"
              variant="outlined"
              error={!!nameError}
              helperText={nameError?.message}
            />
          </Box>
        </Grid>

        {/* 開始日 */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>開始日</Typography>
              <RequiredLabel show={true} />
            </Box>
            <TextField
              required
              fullWidth
              id="period_from"
              name="period_from"
              type="date"
              value={period_from || ""}
              onChange={(e) => handleChange("period_from", e.target.value)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              error={!!periodFromError}
              helperText={periodFromError?.message}
            />
          </Box>
        </Grid>

        {/* 終了日・参画中フラグ */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography>終了日</Typography>
              {!participation_flag && <RequiredLabel show={true} />}
            </Box>
            <TextField
              required={!participation_flag}
              fullWidth
              id="period_at"
              name="period_at"
              type="date"
              value={period_at || ""}
              onChange={(e) => handleChange("period_at", e.target.value)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={participation_flag}
              error={!!periodAtError}
              helperText={periodAtError?.message}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={participation_flag || false}
                  onChange={(e) => {
                    handleChange("participation_flag", e.target.checked);
                    if (e.target.checked) {
                      handleChange("period_at", "");
                      clearErrors("period_at");
                    }
                  }}
                />
              }
              label="参画中"
              sx={{ mt: 1 }}
            />
          </Box>
        </Grid>

        {/* 開発手法（複数選択可） */}
        <Grid item xs={12} md={isScrumSelected ? 8 : 12}>
          <FreeSoloMultiSelect
            label="開発プロセス（複数選択可）"
            inputLabel="開発プロセスを選択"
            name="development_methods"
            value={development_methods}
            onChange={(items) => handleChange("development_methods", items)}
            options={masterData.development_methods || []}
            isRequired
            error={!!developmentMethodError}
            helperText={developmentMethodError?.message}
          />
        </Grid>

        {/* スプリントの周期（スクラム選択時のみ） */}
        {isScrumSelected && (
          <Grid item xs={12} md={4}>
            <NumberInputField
              label="スプリントの周期（週間）"
              name="sprint_cycle_week"
              value={sprint_cycle_week}
              error={sprintCycleWeekError}
              onChange={handleChange}
              required
              placeholder="2"
              min={1}
              maxLength={1}
            />
          </Grid>
        )}

        {/* 役割（複数選択可） */}
        <Grid item xs={12}>
          <Grid item xs={12}>
            <FreeSoloMultiSelect
              label="役割（複数選択可）"
              inputLabel="役割を選択"
              name="roles"
              value={roles}
              onChange={(items) => handleChange("roles", items)}
              options={masterData.roles || []}
              isRequired
              error={!!roleError}
              helperText={roleError?.message}
            />
          </Grid>
        </Grid>

        {/* プロジェクト規模（人） */}
        <Grid item xs={12} sm={6}>
          <NumberInputField
            label="プロジェクト規模（人）"
            name="project_scale"
            value={project_scale}
            error={projectScaleError}
            onChange={handleChange}
            required={true}
            placeholder="100"
            min={1}
            maxLength={6}
          />
        </Grid>

        {/* チーム規模（人） */}
        <Grid item xs={12} sm={6}>
          <NumberInputField
            label="チーム規模（人）"
            name="team_scale"
            value={team_scale}
            error={teamScaleError}
            onChange={handleChange}
            required={true}
            placeholder="5"
            min={1}
            maxLength={6}
          />
        </Grid>

        {/* 業種（プルダウンのチェックボックス・複数選択可） */}
        <Grid item xs={12}>
          <FreeSoloMultiSelect
            label="業種（複数選択可）"
            inputLabel="業種を選択"
            name="industry_ids"
            value={industryValue}
            onChange={(items) =>
              handleChange(
                "industry_ids",
                (items || []).map((i) => i.id).filter((id) => id != null),
              )
            }
            options={masterData.industries || []}
            helperText={"候補から選択してください"}
            freeSolo={false}
            placeholder="業種を選択（任意）"
          />
        </Grid>

        {/* 成果 */}
        <Grid item xs={12}>
          <FormMultilineField
            label={
              <>
                成果/アピールポイント
                <Box
                  component="span"
                  sx={{
                    display: { xs: "block", sm: "inline" },
                  }}
                >
                  （最大500文字）
                </Box>
              </>
            }
            rows={5}
            name="achievement"
            value={achievement}
            onChange={(e) => handleChange("achievement", e.target.value)}
            placeholder="このプロジェクトを通して成し遂げたことやアピールしたいことを記載してください"
            isRequired={false}
            maxLength={500}
            error={false}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
